import { sportTypes } from '@/helpers/sportTypes'
import { icons } from '@/helpers/icons'
import { cleanStatus } from '@/helpers/text'
import { ICONS_RELATED_TO_STATUS, BASE_PARAM_KEYS, LISTINGS_FILTER_CONNECTION_MAP, LISTINGS_PLATFORM_TO_KEY_MAP, FILTER_METADATA, LISTINGS_FILTER_ITEM } from '@/constants'
import whatnotLogoMini from '@/assets/images/logos/whatnot-mini.svg'
import collxLogoMini from '@/assets/images/logos/collx-mini.svg'
import ebayLogoMini from '@/assets/images/logos/ebay-mini.svg'
import shopifyLogoMini from '@/assets/images/logos/shopify-mini.svg'
import { useUserStore } from '@/stores/UserStore'

const uniqueArray = (items, field) => {
  return Array.from(new Set(items.flatMap(item => {
    const fieldValues = item[field]?.split(',') || []
    return fieldValues.length > 0 ? fieldValues : ['']
  })))
}

export const fetchAttributeOptions = (items, filterPlace, store) => {
  if (['listing', 'inventory'].includes(filterPlace)) {
    return store.availableFilters.attribute.reduce((acc, item) => {
      acc.push({ name: item.value, icon: null, count: item.count, selectedFilter: 'Attribute', key_name: item.value.toLowerCase() })
      return acc
    }, [])
  }
  
  const uniqueAttributes = uniqueArray(items, 'attributes').reduce((acc, attribute) => {
    if (attribute !== '' && attribute !== null) {
      const filteredItems = items.filter(item =>
        (item.attributes?.split(',') || []).map(value => value.trim()).includes(attribute)
      )
      const count = filteredItems.length
      acc.push({ name: attribute, icon: null, count, selectedFilter: 'Attribute', key_name: attribute.toLowerCase() })
    } else {
      const filteredItems = items.filter(item => !item.attributes)
      acc.unshift({ name: 'Without Attribute', icon: null, count: filteredItems.length, selectedFilter: 'Attribute' })
    }
    return acc
  }, [])

  const sortedUniqueAttributes = uniqueAttributes.sort((a,b) => a.name.localeCompare(b.name))

  return sortedUniqueAttributes
}

export const fetchFieldFilterOptions = ({ items, filterPlace, store, field, fieldLabel }) => {
  if (!store.availableFilters[field]) return []
  
  if (['listing', 'inventory'].includes(filterPlace)) {
    return store.availableFilters[field].reduce((acc, item) => {
      acc.push({ name: item.value, icon: null, count: item.count, selectedFilter: fieldLabel, key_name: item.value.toLowerCase() })
      return acc
    }, [])
  }

  const uniqueFields = uniqueArray(items, field).reduce((acc, field) => {
    const filteredItems = items.filter(item => (item[field] === null ? '' : item[field]) === field)
    const count = filteredItems.length
    if (field === '') {
      const withoutYearCount = count
      if (withoutYearCount > 0) {
        acc.unshift({ name: `Without ${fieldLabel}`, icon: null, count: withoutYearCount, selectedFilter: fieldLabel })
      }
    } else {
      acc.push({ name: field, icon: null, count, selectedFilter: fieldLabel, key_name: field.toLowerCase() })
    }
    return acc
  }, [])
  
  const sortedUniqueYears = uniqueYears.sort((a,b) => a.name.localeCompare(b.name))

  return sortedUniqueYears
}

export const fetchCategoryOptions = (items, filterPlace, store) => {
  if (['listing', 'inventory'].includes(filterPlace)) {
    return store.availableFilters.category.reduce((acc, item) => {
      acc.push({ name: cleanStatus(item.value.toLowerCase()), icon: null, count: item.count, selectedFilter: 'Categories', key_name: item.value.toLowerCase() })
      return acc
    }, [])
  }
  
  const uniqueSports = new Map()

  items.forEach(item => {
    const category = cleanStatus(item.sport).toLowerCase()
    uniqueSports.set(category, (uniqueSports.get(category) || 0) + 1)
  })

  const filteredSportTypes = sportTypes.filter(type => uniqueSports.has(type.name.toLowerCase()))
    .map(type => ({
      ...type,
      count: uniqueSports.get(type.name.toLowerCase()) || 0,
      key_name: cleanStatus(type.name.toLowerCase()),
      selectedFilter: 'Categories'
    }))
    .sort((a, b) => a.name.localeCompare(b.name))
  return filteredSportTypes
}

export const fetchGradedOptions = (items, filterPlace, store) => {
  if (['listing', 'inventory'].includes(filterPlace)) {
    if (!store.availableFilters.graded) return []

    return store.availableFilters.graded.reduce((acc, item) => {
      acc.push({ name: item.value ? 'Yes' : 'No', icon: null, count: item.count, selectedFilter: 'Graded', key_name: Number(item.value) })
      return acc
    }, [])
  }
 
  const options = [
    { name: 'Yes', icon: null, count: 0, selectedFilter: 'Graded', key_name: 'yes' }, 
    { name: 'No', icon: null, count: 0, selectedFilter: 'Graded', key_name: 'no' }
  ]
  items.forEach(item => {
    options[item.graded === true ? 0 : 1].count++
  })
  return options
}

export const fetchRestockableOptions = (items, filterPlace, store) => {
  if (['listing', 'inventory'].includes(filterPlace)) {
    if (!store.availableFilters.restockable) {
      // Return default Yes/No options if no filter data available
      return [
        { name: 'Yes', icon: null, count: 0, selectedFilter: 'Restockable', key_name: true },
        { name: 'No', icon: null, count: 0, selectedFilter: 'Restockable', key_name: false }
      ]
    }

    return store.availableFilters.restockable.reduce((acc, item) => {
      acc.push({ name: item.value ? 'Yes' : 'No', icon: null, count: item.count, selectedFilter: 'Restockable', key_name: Boolean(item.value) })
      return acc
    }, [])
  }
 
  const options = [
    { name: 'Yes', icon: null, count: 0, selectedFilter: 'Restockable', key_name: true }, 
    { name: 'No', icon: null, count: 0, selectedFilter: 'Restockable', key_name: false }
  ]
  items.forEach(item => {
    options[item.restockable === true ? 0 : 1].count++
  })
  return options
}

export const fetchGraderOptions = (items, filterPlace, store) => {
  if (['listing', 'inventory'].includes(filterPlace)) {
    return store.availableFilters.grader.reduce((acc, item) => {
      acc.push({ name: item.value, icon: null, count: item.count, selectedFilter: 'Grader', key_name: item.value.toLowerCase() })
      return acc
    }, [])
  }
  
  const uniqueGraders = uniqueArray(items, 'grader').reduce((acc, grader) => {
    if (grader !== null && grader !== '') {
      const filteredItems = items.filter(item => item.grader === grader)
      const count = filteredItems.length
      acc.push({ name: grader, icon: null, count, selectedFilter: 'Grader', key_name: grader.toLowerCase() })
    }
    return acc
  }, [])
  .sort((a,b) => a.name.localeCompare(b.name))

  return uniqueGraders
}

export const fetchStatusTypesOptions = (items, filterPlace, store, additionalStore) => {
  if (filterPlace === 'listing') {
    return [
      { name: 'For sale', icon: null, count: additionalStore.listings.length, selectedFilter: 'Status', key_name: 'for_sale' },
      { name: 'Ended', icon: null, count: additionalStore.endedListings.length, selectedFilter: 'Status', key_name: 'ended' }
    ]
  }

  if (filterPlace === 'inventory') {
    return Object.keys(store.availableFilters.status).reduce((acc, item) => {
      acc.push({ name: cleanStatus(item), icon: null, count: store.availableFilters.status[item], selectedFilter: 'Status', key_name: item.toLowerCase() })
      return acc
    }, [])
  }

  const uniqueStatuses = uniqueArray(items, 'status').reduce((acc, status) => {
    if (status !== null && status !== '') {
      const filteredItems = items.filter(item => item.status === status)
      const count = filteredItems.length
      acc.push({ name: cleanStatus(status), icon: icons[ICONS_RELATED_TO_STATUS[status]], count, selectedFilter: 'Status', key_name: status })
    }
    return acc
  }, [])

  return uniqueStatuses
}

export const fetchListingOptions = (items, filterPlace, store) => {
  if (['inventory'].includes(filterPlace)) {
    return [
      { name: 'Ebay', src: ebayLogoMini, selectedFilter: 'Listing', key_name: 'ebayListed' },
      { name: 'CollX', src: collxLogoMini, selectedFilter: 'Listing', key_name: 'collxListed' },
      { name: 'Whatnot', src: whatnotLogoMini, selectedFilter: 'Listing', key_name: 'whatnotListed' },
      { name: 'Shopify', src: shopifyLogoMini, selectedFilter: 'Listing', key_name: 'shopifyListed' },
      { name: 'No Listings', src: null, selectedFilter: 'Listing', key_name: 'hasNoListings' },
    ]
  }
}

export const generateQueryString = (data) => {
  // Initialize an empty object to store the result
  const result = {}

  // Iterate over each property in the data object
  Object.entries(data).forEach(([key, value]) => {
    // Handle customField specially - comma-separated format
    // URL will be: ?customField=Location:Shelf,Rare:Box
    if (key === 'customField') {
      result.customField = value.map(item => item.key_name).join(',')
      return
    }
    
    // Handle Restockable: condition 'is' → true, 'is_not' → false
    if (key === 'Restockable') {
      const condition = value[0]?.restockableCondition || 'is_not'
      result.restockable = condition === 'is' ? 'true' : 'false'
      return
    }

    // Handle Listings: group by listingsGroupId. Same group = OR (comma), different groups = AND (multiple params)
    // OR/AND grouping only applies when exactly 2 items share a condition.
    // When 3+ share a condition, all are treated as AND (each gets its own param).
    // e.g. listedOn=ebay,collx&listedOn=whatnot&notListedOn=shopify
    if (key === 'Listing') {
      // Count items per condition
      const conditionCounts = {}
      value.forEach(item => {
        const cond = item.listingsCondition || 'is'
        conditionCounts[cond] = (conditionCounts[cond] || 0) + 1
      })

      const listedOnByGroup = {} // groupId -> [platforms]
      const notListedOnByGroup = {}
      let autoGroupCounter = 1000 // high offset for auto-assigned unique groups

      value.forEach(item => {
        const platform = LISTINGS_FILTER_CONNECTION_MAP[item.key_name] || item.key_name
        const condition = item.listingsCondition || 'is'

        // When 3+ items share a condition, force each into its own group (AND)
        // When exactly 2, respect the listingsGroupId (allows OR or AND)
        const groupId = conditionCounts[condition] > 2
          ? autoGroupCounter++
          : (item.listingsGroupId ?? 1)

        if (condition === 'is_not') {
          if (!notListedOnByGroup[groupId]) notListedOnByGroup[groupId] = []
          notListedOnByGroup[groupId].push(platform)
        } else {
          if (!listedOnByGroup[groupId]) listedOnByGroup[groupId] = []
          listedOnByGroup[groupId].push(platform)
        }
      })

      const listedOnGroups = Object.values(listedOnByGroup).map(platforms => platforms.join(','))
      const notListedOnGroups = Object.values(notListedOnByGroup).map(platforms => platforms.join(','))

      if (listedOnGroups.length > 0) {
        result.listedOn = listedOnGroups.length === 1 ? listedOnGroups[0] : listedOnGroups
      }
      if (notListedOnGroups.length > 0) {
        result.notListedOn = notListedOnGroups.length === 1 ? notListedOnGroups[0] : notListedOnGroups
      }
      return
    }
   
    let key_ = key
    // Extract key_name values from each array and join them into a string
    const keyNamesString = value.map(item => {
      let keyName = item.key_name

      if (keyName === 'with_errors' || keyName === 'with_suggested_edits' || keyName === 'yes') {
        keyName = 1
      }

      if (keyName === 'no' || keyName === 'viewed') {
        keyName = 0
      }

      if (key_ === 'Categories') {
        key_ = 'category'
      }

      if (keyName === 'draft') {
        keyName = 'draft'
      }

      if (keyName === '' || keyName === null ) {
        keyName = ''
      }

      return keyName
    }).join(',')
    
    // Assign the comma-separated string to the corresponding key in the result object
    if (!BASE_PARAM_KEYS.includes(key_)) {
      result[key_.toLocaleLowerCase()] = keyNamesString
    } else if (key !== 'Listing') {
      result[key_] = keyNamesString
    }
  })

  return result
}
/**
 * Build a URLSearchParams string from an object, properly handling array values.
 * Array values produce repeated params: { listedOn: ['ebay,collx', 'whatnot'] }
 * → "listedOn=ebay%2Ccollx&listedOn=whatnot"
 */
export const buildSearchParams = (params) => {
  const usp = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach(v => usp.append(key, v))
    } else if (value !== undefined && value !== null && value !== '') {
      usp.append(key, value)
    }
  })
  return usp.toString()
}

export const extractBaseParams = (itemFilters, baseParamKeys) => {
  const baseParams = {}
  const filterParams = {}

  Object.entries(itemFilters).forEach(([key, value]) => {
    if (baseParamKeys.includes(key)) {
      if (value !== undefined && value !== null && value !== '') {
        baseParams[key] = value
      }
    } else {
      filterParams[key] = value
    }
  })

  return { baseParams, filterParams }
}

export const getItemFiltersWithoutSortBy = (filters) => { 
  return Object.keys(filters).reduce((acc, item) => {
    if (item !== 'sortBy') {
      acc[item] = filters[item]
    }
    return acc
  }, {})
}

export const getUserViewSettings = () => {
  const userStore = useUserStore()
  const displayColumnsJson = localStorage.getItem('display_columns_inventory')
  const displayColumnsData = displayColumnsJson ? JSON.parse(displayColumnsJson) : {}
  const columns = displayColumnsData[userStore.user.id] || null
  
  const sortJson = localStorage.getItem('inventorySort')
  const sortData = sortJson ? JSON.parse(sortJson) : {}
  const sort = sortData[userStore.user.id] || null

  return { columns, sort }
}

export const parseBackendFilters = (backendFilters) => {
  const result = {}
  const badgeIcon = LISTINGS_FILTER_ITEM.icon

  // Handle listedOn / notListedOn URL params (e.g. listedOn=ebay,collx or listedOn: ['ebay,collx', 'whatnot'])
  const parseListedOnParam = (paramValue, condition) => {
    if (!paramValue) return
    // paramValue can be a string ('ebay,collx') or an array (['ebay,collx', 'whatnot']) for AND groups
    const groups = Array.isArray(paramValue) ? paramValue : [paramValue]

    if (!result['Listing']) result['Listing'] = []

    groups.forEach((group, groupIndex) => {
      const platforms = String(group).split(',')
      // Items in the same group get the same groupId (OR), different groups get different groupIds (AND)
      const groupId = groupIndex + 1

      platforms.forEach(platform => {
        const trimmed = platform.trim()
        if (!trimmed) return
        const keyName = LISTINGS_PLATFORM_TO_KEY_MAP[trimmed]
        if (!keyName) return

        const displayName = FILTER_METADATA[keyName]?.displayName || trimmed
        result['Listing'].push({
          name: displayName,
          icon: null,
          count: 0,
          selectedFilter: 'Listing',
          selectedFilterIcon: badgeIcon,
          key_name: keyName,
          listingsCondition: condition,
          listingsGroupId: groupId
        })
      })
    })
  }

  parseListedOnParam(backendFilters.listedOn, 'is')
  parseListedOnParam(backendFilters.notListedOn, 'is_not')

  Object.entries(backendFilters).forEach(([key, value]) => {
    if (!value && value !== 0) return

    // Skip listedOn/notListedOn — already handled above
    if (key === 'listedOn' || key === 'notListedOn') return

    const metadata = FILTER_METADATA[key]
    if (!metadata) return

    const filterName = metadata.selectedFilter

    // Resolve icon fields from metadata:
    // metadata.icon → item-level icon (e.g. category, brand, attribute, year, status)
    // metadata.selectedFilterIcon → badge-level icon (e.g. graded, listings, price, etc.)
    const itemIcon = metadata.icon || null
    const badgeIcon = metadata.selectedFilterIcon || metadata.icon || null

    // Handle price filters - use key as result key (priceMin, priceMax, priceBetween)
    if (metadata.isPrice) {
      if (metadata.isBetween) {
        const [min, max] = String(value).split(',')
        result[key] = [{
          name: `$${min} - $${max}`,
          icon: itemIcon,
          selectedFilterIcon: badgeIcon,
          selectedFilter: filterName,
          key_name: value
        }]
      } else {
        result[key] = [{
          name: `$${value}`,
          icon: itemIcon,
          selectedFilterIcon: badgeIcon,
          selectedFilter: filterName,
          key_name: value
        }]
      }
      return
    }

    // Handle quantity filters - same pattern as price
    if (metadata.isQuantity) {
      if (metadata.isBetween) {
        const [min, max] = String(value).split(',')
        result[key] = [{
          name: `${min} - ${max}`,
          icon: itemIcon,
          selectedFilterIcon: badgeIcon,
          selectedFilter: filterName,
          key_name: value
        }]
      } else {
        result[key] = [{
          name: String(value),
          icon: itemIcon,
          selectedFilterIcon: badgeIcon,
          selectedFilter: filterName,
          key_name: value
        }]
      }
      return
    }

    // Handle restockable (boolean switch: true/false)
    if (metadata.isRestockable) {
      const boolValue = value === true || value === 'true'
      result['Restockable'] = [{
        name: boolValue ? 'Yes' : 'No',
        key_name: boolValue,
        selectedFilter: 'Restockable',
        selectedFilterIcon: badgeIcon,
        icon: itemIcon
      }]
      return
    }

    // Handle custom fields (format: "FieldName:Value,OtherField:OtherValue")
    if (metadata.isCustomField) {
      const pairs = String(value).split(',')
      result['customField'] = pairs.map(pair => {
        const [fieldName, fieldValue] = pair.split(':')
        return {
          customFieldName: fieldName,
          customFieldValue: fieldValue || '',
          key_name: `${fieldName}:${fieldValue || ''}`,
          selectedFilter: 'customField',
          selectedFilterIcon: badgeIcon,
          icon: itemIcon
        }
      })
      return
    }

    if (!result[filterName]) {
      result[filterName] = []
    }

    // Handle listing filters (boolean flags) — include listingsCondition + listingsGroupId
    if (metadata.isListing) {
      if (value === true || value === 'true') {
        result[filterName].push({
          name: metadata.displayName || key,
          icon: itemIcon,
          count: 0,
          selectedFilter: filterName,
          selectedFilterIcon: badgeIcon,
          key_name: key,
          listingsCondition: 'is',
          listingsGroupId: 1
        })
      }
      return
    }

    // Handle regular filters - split comma-separated values
    const values = String(value).split(',')
    
    values.forEach(v => {
      const trimmedValue = v.trim()
      if (!trimmedValue) return

      result[filterName].push({
        name: trimmedValue,
        icon: itemIcon,
        count: 0,
        selectedFilter: filterName,
        selectedFilterIcon: badgeIcon,
        key_name: trimmedValue.toLowerCase()
      })
    })
  })

  return result
}

